import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import api, { formatApiError } from "@/lib/api";
import { fmtNum } from "@/utils/toEnglishDigits";
import { fmtDate } from "@/utils/date";
import { formatPhoneDisplay } from "@/utils/phoneFormat";
import MultiPhoneInput, { hydratePhones, serializePhones, validatePhonesList } from "@/components/MultiPhoneInput";
import { Buildings, MagnifyingGlass, MapPin, ShieldWarning, ArrowRight, Plus, X, Copy, CheckCircle, Warning, ClipboardText } from "@phosphor-icons/react";

const STATUS_LABEL = {
  all: "الكل",
  approved: "معتمدة",
  pending: "قيد المراجعة",
  rejected: "مرفوضة",
  blocked: "محظورة",
  admin: "أدمن",
};
const STATUS_STYLE = {
  approved: "bg-[#2E7D32]/15 border-[#2E7D32]/50 text-[#2E7D32]",
  pending: "bg-[#F57F17]/15 border-[#F57F17]/50 text-[#F57F17]",
  rejected: "bg-[#C62828]/15 border-[#C62828]/50 text-[#C62828]",
  blocked: "bg-white/5 border-white/20 text-white/60",
  admin: "bg-primary/15 border-primary/50 text-primary",
};

const STATUS_CHIPS = ["all", "approved", "pending", "rejected", "blocked", "admin"];

export default function CompaniesAdmin() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusF, setStatusF] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [credsModal, setCredsModal] = useState(null); // { email, password, message, needs_completion }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/companies");
      setRows(data);
    } catch {
      toast.error("تعذر التحميل");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const statusCounts = useMemo(() => {
    const c = { all: rows.length };
    ["approved", "pending", "rejected", "blocked", "admin"].forEach((k) => {
      c[k] = rows.filter((r) => r.status === k).length;
    });
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    let list = rows;
    if (statusF && statusF !== "all") list = list.filter((r) => r.status === statusF);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (r) =>
          (r.name || "").toLowerCase().includes(q) ||
          (r.contact_person_name || "").toLowerCase().includes(q) ||
          (r.email || "").toLowerCase().includes(q) ||
          (r.phone || "").includes(q),
      );
    }
    return list;
  }, [rows, search, statusF]);

  return (
    <div className="min-h-screen bg-background text-foreground" dir="rtl">
      <TopBar />
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-5 flex items-end justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">الشركات المعتمدة</h1>
            <p className="text-white/50 text-sm mt-1">
              <span className="num">{fmtNum(filtered.length)}</span> شركة ظاهرة ·{" "}
              <span className="num">{fmtNum(rows.length)}</span> إجمالي
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            data-testid="btn-add-company"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground font-bold hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
          >
            <Plus size={18} weight="bold" />
            إضافة شركة جديدة
          </button>
        </div>

        {/* Search */}
        <div className="relative max-w-xl mb-4">
          <MagnifyingGlass size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-primary/60" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="بحث بالاسم أو البريد أو الهاتف…"
            className="mz-input pr-9"
            data-testid="companies-search"
          />
        </div>

        {/* Status chips */}
        <div className="mb-5">
          <div className="text-primary font-bold text-sm mb-2">🏷️ الحالة:</div>
          <div dir="rtl" className="flex flex-row items-center flex-wrap gap-2" data-testid="companies-status-chips">
            {STATUS_CHIPS.map((k) => {
              const active = statusF === k;
              const count = statusCounts[k] || 0;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setStatusF(k)}
                  data-testid={`chip-company-status-${k}`}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm border transition-all whitespace-nowrap ${
                    active
                      ? "bg-primary text-primary-foreground border-primary font-bold"
                      : "bg-primary/5 border-primary/30 text-white hover:bg-primary/15 font-semibold"
                  }`}
                >
                  <span>{STATUS_LABEL[k]}</span>
                  <span
                    className={`num text-[11px] font-bold px-2 py-0.5 rounded-full ${
                      active ? "bg-[#0A1936]/25 text-primary-foreground" : "bg-white/10 text-white"
                    }`}
                  >
                    {fmtNum(count)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-primary animate-pulse">جارٍ التحميل…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-white/50 bg-card border border-primary/15 rounded-2xl">لا توجد شركات</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map((c, i) => (
              <Link
                key={c.id}
                to={`/admin/companies/${c.id}`}
                data-testid={`company-${c.id}`}
                className="fadeup bg-card border border-primary/15 rounded-2xl p-5 hover:border-primary/50 hover:shadow-[0_10px_30px_-10px_rgba(212,175,55,0.35)] transition-all group"
                style={{ animationDelay: `${Math.min(i, 12) * 30}ms` }}
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center flex-shrink-0">
                    <Buildings size={22} weight="duotone" className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-bold truncate group-hover:text-primary transition-colors">
                        {c.name}
                      </span>
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full border ${
                          STATUS_STYLE[c.status] || "bg-white/5 border-white/20"
                        }`}
                      >
                        {STATUS_LABEL[c.status] || c.status}
                      </span>
                    </div>
                    <div className="text-white/60 text-sm mt-1 truncate">{c.email}</div>
                    {c.contact_person_name && (
                      <div className="text-primary/80 text-xs mt-1 truncate" data-testid={`company-contact-${c.id}`}>
                        👤 {c.contact_person_name}
                      </div>
                    )}
                    <div className="text-white/50 text-xs mt-1 num" dir="ltr">
                      {c.phone ? formatPhoneDisplay(c.phone) : "—"}
                    </div>
                    <div className="text-white/40 text-xs mt-2 flex items-center gap-3">
                      <span>
                        حجوزات:{" "}
                        <span className="num text-primary font-bold">{fmtNum(c.bookings_count || 0)}</span>
                      </span>
                      {c.created_at && (
                        <span>
                          · <span className="num">{fmtDate(c.created_at)}</span>
                        </span>
                      )}
                    </div>
                    {c.address && (
                      <div className="text-white/40 text-xs mt-1 flex items-center gap-1">
                        <MapPin size={12} />
                        {c.address}
                      </div>
                    )}
                  </div>
                  <ArrowRight
                    size={18}
                    className="text-primary/50 group-hover:text-primary group-hover:-translate-x-1 transition-all flex-shrink-0"
                    weight="bold"
                  />
                </div>
                {c.status === "admin" && (
                  <div className="mt-3 pt-3 border-t border-primary/10 text-center text-primary text-xs inline-flex items-center gap-1.5 justify-center w-full">
                    <ShieldWarning size={13} weight="fill" /> حساب أدمن — لا يخضع للاعتماد
                  </div>
                )}
                {/* 2026-07 §CompanyBookingsShortcut: quick link to filtered
                    bookings for approved companies. Uses onClick +
                    stopPropagation so it doesn't trigger the wrapping <Link>
                    to /admin/companies/:id. */}
                {c.status === "approved" && Number(c.bookings_count || 0) > 0 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const q = new URLSearchParams({
                        filter_company_id: c.id,
                        filter_company_name: c.name || c.email || "",
                      }).toString();
                      navigate(`/company-bookings?${q}`);
                    }}
                    data-testid={`company-view-bookings-${c.id}`}
                    className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-primary/40 bg-primary/5 text-primary hover:bg-primary/15 text-xs font-bold transition-colors"
                  >
                    <ClipboardText size={13} weight="duotone" />
                    عرض حجوزات الشركة
                    (<span className="num">{fmtNum(c.bookings_count || 0)}</span>)
                  </button>
                )}
              </Link>
            ))}
          </div>
        )}
      </main>

      {showAddModal && (
        <AddCompanyModal
          onClose={() => setShowAddModal(false)}
          onSuccess={(data) => {
            setShowAddModal(false);
            setCredsModal(data);
            load();
          }}
        />
      )}

      {credsModal && (
        <CredentialsModal data={credsModal} onClose={() => setCredsModal(null)} />
      )}
    </div>
  );
}

// ============ Add Company Modal ============
function AddCompanyModal({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    company_name: "",
    email: "",
    cr_number: "",
    contact_name: "",
    password: "",
    notes: "",
    auto_approve: true,
  });
  const [phones, setPhones] = useState(hydratePhones([], ""));
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setFormData((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!formData.company_name.trim()) {
      toast.error("اسم الشركة مطلوب");
      return;
    }
    const phoneError = validatePhonesList(phones, { requireAtLeastOne: false });
    if (phoneError) return toast.error(phoneError);
    setSaving(true);
    try {
      const phonesArr = serializePhones(phones);
      const primary = phonesArr.find((p) => p.is_primary)?.number || phonesArr[0]?.number || "";
      const rest = phonesArr.filter((p) => p.number && p.number !== primary).map((p) => p.number);
      const body = {
        company_name: formData.company_name.trim(),
        auto_approve: !!formData.auto_approve,
      };
      if (formData.email.trim()) body.email = formData.email.trim();
      if (formData.cr_number.trim()) body.cr_number = formData.cr_number.trim();
      if (formData.contact_name.trim()) body.contact_name = formData.contact_name.trim();
      if (primary) body.phone = primary;
      if (rest.length) body.phones = rest;
      if (formData.password.trim()) body.password = formData.password.trim();
      if (formData.notes.trim()) body.notes = formData.notes.trim();
      const { data } = await api.post("/admin/companies/create", body);
      toast.success(data?.message || "تم إضافة الشركة بنجاح");
      onSuccess({
        email: data.email,
        password: data.password,
        generated_password: data.generated_password,
        needs_completion: data.needs_completion,
        message: data.message,
      });
    } catch (err) {
      toast.error("تعذّر الإضافة: " + formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      dir="rtl"
      onClick={onClose}
      data-testid="add-company-modal"
    >
      <div
        className="bg-card border border-primary/30 rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-primary/20">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-primary/15 border border-primary/40 flex items-center justify-center">
              <Buildings size={18} weight="duotone" className="text-primary" />
            </div>
            <h2 className="text-white font-bold text-lg">إضافة شركة جديدة</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-white/10 text-white/70 flex items-center justify-center"
            data-testid="add-company-close"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        <form onSubmit={submit} className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Section 1 — basic info */}
          <div>
            <div className="text-primary font-bold text-sm mb-2">1) المعلومات الأساسية</div>
            <div className="space-y-3">
              <FieldRow label="اسم الشركة *" testId="fld-company-name">
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => set("company_name", e.target.value)}
                  placeholder="شركة الأنفال للسياحة"
                  className="mz-input"
                  required
                  autoFocus
                  data-testid="input-company-name"
                />
              </FieldRow>
              <FieldRow label="البريد الإلكتروني (اختياري)">
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="example@company.com"
                  className="mz-input"
                  dir="ltr"
                  data-testid="input-email"
                />
              </FieldRow>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FieldRow label="رقم السجل التجاري (اختياري)">
                  <input
                    type="text"
                    value={formData.cr_number}
                    onChange={(e) => set("cr_number", e.target.value)}
                    placeholder="1234567890"
                    className="mz-input num"
                    dir="ltr"
                    data-testid="input-cr"
                  />
                </FieldRow>
                <FieldRow label="اسم مسؤول التواصل (اختياري)">
                  <input
                    type="text"
                    value={formData.contact_name}
                    onChange={(e) => set("contact_name", e.target.value)}
                    placeholder="محمد المدير"
                    className="mz-input"
                    data-testid="input-contact"
                  />
                </FieldRow>
              </div>
            </div>
          </div>

          {/* Section 2 — phones */}
          <div>
            <div className="text-primary font-bold text-sm mb-2">2) بيانات التواصل</div>
            <MultiPhoneInput value={phones} onChange={setPhones} testIdPrefix="add-company-phone" />
          </div>

          {/* Section 3 — admin settings */}
          <div>
            <div className="text-primary font-bold text-sm mb-2">3) إعدادات إدارية</div>
            <div className="space-y-3">
              <FieldRow label="كلمة المرور (اختياري)" hint="سيتم توليدها تلقائياً إن تُركت فارغة">
                <input
                  type="text"
                  value={formData.password}
                  onChange={(e) => set("password", e.target.value)}
                  placeholder="اتركها فارغة لتوليد تلقائي"
                  className="mz-input"
                  dir="ltr"
                  autoComplete="off"
                  data-testid="input-password"
                />
              </FieldRow>
              <FieldRow label="ملاحظات إدارية (اختياري)">
                <textarea
                  value={formData.notes}
                  onChange={(e) => set("notes", e.target.value)}
                  rows={2}
                  className="mz-input resize-none"
                  placeholder="أي ملاحظات داخلية…"
                  data-testid="input-notes"
                />
              </FieldRow>
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg bg-primary/5 border border-primary/25 hover:bg-primary/10 transition">
                <button
                  type="button"
                  onClick={() => set("auto_approve", !formData.auto_approve)}
                  data-testid="toggle-auto-approve"
                  className={`w-6 h-6 rounded border-2 flex items-center justify-center shrink-0 transition ${
                    formData.auto_approve ? "bg-primary border-primary" : "border-white/40 bg-transparent"
                  }`}
                >
                  {formData.auto_approve && <CheckCircle size={14} weight="fill" className="text-primary-foreground" />}
                </button>
                <div className="flex-1">
                  <div className="text-white font-bold text-sm">اعتماد تلقائي</div>
                  <div className="text-white/50 text-xs mt-0.5">
                    الشركة ستكون معتمدة فوراً دون خطوة قبول لاحقة
                  </div>
                </div>
              </label>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-primary/20 bg-[#0B1A3A]/40">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-lg border border-white/20 text-white/80 hover:bg-white/5 text-sm"
            data-testid="btn-cancel-add"
          >
            إلغاء
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving || !formData.company_name.trim()}
            className="px-5 py-2 rounded-lg bg-primary text-primary-foreground font-bold hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2 text-sm"
            data-testid="btn-submit-add"
          >
            {saving ? "جاري الإضافة…" : (<><Plus size={14} weight="bold" /> إضافة الشركة</>)}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ Credentials Modal ============
function CredentialsModal({ data, onClose }) {
  const [copiedField, setCopiedField] = useState(null);
  const copy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
      toast.success("تم النسخ");
    } catch {
      toast.error("تعذّر النسخ");
    }
  };
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      dir="rtl"
      data-testid="credentials-modal"
    >
      <div className="bg-card border border-primary/40 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
        <div className="px-5 py-4 border-b border-primary/20 flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-green-500/15 border border-green-500/40 flex items-center justify-center">
            <CheckCircle size={18} weight="fill" className="text-green-400" />
          </div>
          <h2 className="text-white font-bold text-lg">تمت الإضافة بنجاح</h2>
        </div>
        <div className="p-5 space-y-4">
          {data.message && (
            <div className="text-white/80 text-sm bg-green-500/10 border border-green-500/25 rounded-lg p-3">
              {data.message}
            </div>
          )}
          {data.needs_completion && (
            <div className="text-orange-300 text-sm bg-orange-500/10 border border-orange-500/40 rounded-lg p-3 flex items-start gap-2">
              <Warning size={16} weight="fill" className="text-orange-300 shrink-0 mt-0.5" />
              <div>بعض البيانات ناقصة — يمكنك إكمالها لاحقاً من زر التعديل داخل ملف الشركة.</div>
            </div>
          )}
          <div>
            <div className="text-white/60 text-xs mb-1">البريد الإلكتروني</div>
            <div className="flex items-center gap-2 bg-[#0B1A3A] border border-primary/25 rounded-lg px-3 py-2">
              <div className="flex-1 text-white text-sm truncate num" dir="ltr" data-testid="creds-email">
                {data.email}
              </div>
              <button
                type="button"
                onClick={() => copy(data.email, "email")}
                className="text-primary hover:text-primary/80 shrink-0"
                data-testid="btn-copy-email"
                title="نسخ"
              >
                {copiedField === "email" ? <CheckCircle size={16} weight="fill" /> : <Copy size={16} />}
              </button>
            </div>
          </div>
          {data.password && (
            <div>
              <div className="text-white/60 text-xs mb-1">كلمة المرور</div>
              <div className="flex items-center gap-2 bg-[#0B1A3A] border-2 border-primary/60 rounded-lg px-3 py-2">
                <div
                  className="flex-1 text-primary text-base font-bold num tracking-wider"
                  dir="ltr"
                  data-testid="creds-password"
                  style={{ fontFamily: 'ui-monospace, "SF Mono", "Cascadia Code", Consolas, monospace' }}
                >
                  {data.password}
                </div>
                <button
                  type="button"
                  onClick={() => copy(data.password, "password")}
                  className="text-primary hover:text-primary/80 shrink-0"
                  data-testid="btn-copy-password"
                  title="نسخ"
                >
                  {copiedField === "password" ? <CheckCircle size={16} weight="fill" /> : <Copy size={16} />}
                </button>
              </div>
              <div className="text-yellow-400 text-xs mt-2 flex items-center gap-1">
                <Warning size={12} weight="fill" />
                انسخ كلمة المرور الآن — لن تظهر مرة أخرى.
              </div>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-primary/20 bg-[#0B1A3A]/40 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 rounded-lg bg-primary text-primary-foreground font-bold hover:bg-primary/90 text-sm"
            data-testid="btn-close-creds"
          >
            تم
          </button>
        </div>
      </div>
    </div>
  );
}

function FieldRow({ label, hint, children, testId }) {
  return (
    <div data-testid={testId}>
      <label className="text-white/80 text-xs font-semibold mb-1 block">{label}</label>
      {children}
      {hint && <div className="text-white/40 text-[11px] mt-1">{hint}</div>}
    </div>
  );
}
